const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/Forms/utils/validation.ts';
import { IntlShape, MessageDescriptor } from 'react-intl';
import textMap from './translations';
import { isEmpty } from '../../../utils/generalUtils';

/**
 * Message displayed below a field when its validation failed
 */
export interface ValidationMsg {
    hint: MessageDescriptor;
    hintValues?: { [key: string]: string | number };
}

/**
 * The result of a validation is either true (indicating success) or a message (indicating failure).
 */
export type ValidationResult = undefined | ValidationMsg;

/**
 * A validation function takes a field's value and returns a string which should be an textMap-ID
 */
export type ValidationFunction = (value: any) => MessageDescriptor | string | undefined;

/**
 * The type of validation may be one of the standard validation strings, or a custom function
 */
export type ValidationType = undefined | ValidationFunction;

/**
 * if more than one validator is used
 */
export const composeValidators =
    (...validators: ({ (value: any): any } | undefined)[]) =>
    (value: any) =>
        validators.reduce((error, validator) => error || validator?.(value), undefined);

export const composeValidatorsAllVals =
    (...validators: ({ (value: any, allValues: any): any } | undefined)[]) =>
    (value: any, allValues: any) =>
        validators.reduce((error, validator) => error || validator?.(value, allValues), undefined);

/**
 * @return The validation message as a string, or an empty string if the result is true
 */

export function valResToString(
    hint: string | undefined | ValidationResult,
    formatMessage: IntlShape['formatMessage']
): string | undefined {
    if (hint === undefined) {
        return '';
    } else if (typeof hint === 'string') {
        return formatMessage({ id: hint });
    } else if (hint.hint && typeof hint.hint === 'object') {
        return formatMessage(hint.hint, hint.hintValues);
    }
}

/**
 * This function returns a validation function for validating a number field
 */
export const validateNumber =
    (mustBeInt: boolean | undefined, minValue: number | undefined, maxValue: number | undefined) =>
    (value: string): ValidationResult => {
        if (!/^[-+]?[0-9]*(\.[0-9]*)?$/.test(value)) {
            return { hint: textMap.notANumber };
        }

        const n = Number(value);

        if (mustBeInt && Math.floor(n) !== n) {
            return { hint: textMap.notAnInt };
        }

        if (minValue !== undefined && n < minValue) {
            return { hint: textMap.mustBeAtLeast, hintValues: { min: minValue } };
        }

        if (maxValue !== undefined && n > maxValue) {
            return { hint: textMap.mustBeLeqThan, hintValues: { max: maxValue } };
        }

        return undefined;
    };

// very important to return a string or undefined (-> returning an object causes rff to rerender
// every field EVERY time you enter stuff into field X. legacy example: { hint: textMap.emptyNotAllowed }
export const validateNotEmpty = (value: any) => (value ? undefined : textMap.emptyNotAllowed.id);

export const validateNotEmptyIfRequired = (isRequiredFn: (allVal: any) => boolean) => (value: any, allVals: any) =>
    isRequiredFn(allVals) ? (value ? undefined : textMap.emptyNotAllowed.id) : undefined;

/**
 * user didnt choose a value from a list (Dropdown)
 */

export const validateNotEmptyList = (value: any) => (value ? undefined : textMap.emptyNotAllowedList.id);

/**
 * user didnt choose a value from a Radiogroup
 */
export const validateNotEmptyRadios = (value: any) => (value ? undefined : textMap.emptyNotAllowedRadio.id);

/**
 * return hint if email isn't correct
 */

export const validateEmail = (value: string): MessageDescriptor | undefined => {
    if (!value.match(/^[\w-+.]+@([\w-]+\.)+[\w-]{2,}$/)) {
        return textMap.emailInvalid;
    }
};

export const validateEmailRFF = (value: any) => {
    if (value) {
        const messageDescriptor = validateEmail(value);
        return messageDescriptor ? messageDescriptor.id : undefined;
    }
};

export const validateAHV = (value: string): MessageDescriptor | undefined => {
    if (!value.match(/^756\.\d{4}\.\d{4}\.\d{2}$/)) {
        return textMap.ahvInvalid;
    }
};

export const validateAHVNoDots = (value: string) => {
    if (!value.match(/^756\d{10}$/)) {
        return textMap.ahvInvalidNoDots;
    }
};

export const validateAHVRFF = (value: any) => {
    if (value) {
        const messageDescriptor = validateAHV(value);
        return messageDescriptor ? messageDescriptor.id : undefined;
    }
};

export const validatePhoneNumber = (value: string) => {
    // This doesn't really validate, but it is what we currently have
    return validatePhoneNumberLength(value);
};

export const validatePhoneNumberLength = (value: string): MessageDescriptor | undefined => {
    const validationValue = value.replace(/\s/g, '');
    if (validationValue.length < 12 || 16 < validationValue.length) {
        return textMap.phoneNumberInvalid;
    }
};

export const validatePhoneNumberLengthIsValidRFF: ValidationFunction = value => {
    const errorMessage = validatePhoneNumberEmptyIsInvalid(value);
    if (errorMessage === undefined) {
        if (value) {
            const messageDescriptor = validatePhoneNumberLength(value);
            return messageDescriptor ? messageDescriptor.id : undefined;
        }
    } else {
        return errorMessage;
    }
};

export const isValidPhoneNumber = (value: string) => {
    return !validatePhoneNumber(value);
};

/**
 * return hint if value is empty respectively not long enough to be a kind of phone number
 */
export const validatePhoneNumberEmptyIsInvalid: ValidationFunction = value => {
    const errorHint = textMap.emptyNotAllowed.id;
    if (!value || value.length < 4) {
        return errorHint;
    }
};

/**
 * return hint if both phoneNumbers are empty
 * defaultProperties are: 'tel' 'mobile'
 */

export const validateNeedAtLeastOnePhoneNumber = (propsTels: string[][]) => (values: any) => {
    if (isEmpty(values)) {
        return undefined;
    }
    const errors: any = {};
    propsTels.forEach(sectionTels => {
        const hasErrorMsgs = sectionTels.reduce(
            (prev, cur: string) => !isEmpty(validatePhoneNumberEmptyIsInvalid(values[cur])) && prev,
            true
        );
        if (hasErrorMsgs) {
            sectionTels.forEach((val: string) => (errors[val] = textMap.passAtLeastOnTel.id));
        }
    });
    return errors;
};
